import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  RefreshCw,
  Edit3,
  Save,
  X,
  Globe,
  Sparkles,
  CheckCircle,
  AlertCircle,
  Zap,
  ExternalLink
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import useBrandUrl from '../hooks/useBrandUrl';
import { useBrandData } from '../useBrandData';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock } from 'lucide-react';
import { 
  getTimezoneOptions, 
  getTimezoneDisplayName, 
  getCurrentTimeInTimezone, 
  formatTimeInTimezone,
  isValidTimezone 
} from '@/lib/timezone-utils';

interface GlobalSettingsTabProps {
  brandId: string | undefined;
  selectedBrand: any;
  setSelectedBrand: (brand: any) => void;
}

const GlobalSettingsTab: React.FC<GlobalSettingsTabProps> = ({
  brandId,
  selectedBrand,
  setSelectedBrand
}) => {
  const { toast } = useToast();
  const [editingBrandName, setEditingBrandName] = useState(false);
  const [brandNameValue, setBrandNameValue] = useState(selectedBrand?.brand_name || '');
  const [selectedTimezone, setSelectedTimezone] = useState(selectedBrand?.timezone || 'America/Chicago');
  const [isUpdatingTimezone, setIsUpdatingTimezone] = useState(false);
  const [editingLandingPage, setEditingLandingPage] = useState(false);
  const [landingPageValue, setLandingPageValue] = useState(selectedBrand?.default_landing_page || '');
  const { updateBrandField } = useBrandData(brandId);
  
  const { 
    brandUrl, 
    setBrandUrl, 
    isReanalyzing, 
    loading: urlLoading, 
    updateBrandUrl, 
    reanalyzeWebsite 
  } = useBrandUrl(brandId, setSelectedBrand);
  
  const loading = urlLoading;

  // Sync timezone state when selectedBrand changes
  useEffect(() => {
    if (selectedBrand?.timezone) {
      setSelectedTimezone(selectedBrand.timezone);
    }
  }, [selectedBrand?.timezone]);

  // Sync landing page state when selectedBrand changes
  useEffect(() => {
    setLandingPageValue(selectedBrand?.default_landing_page || '');
  }, [selectedBrand?.default_landing_page]);

  const handleTimezoneChange = async (newTimezone: string) => {
    if (!brandId || !newTimezone || !isValidTimezone(newTimezone)) {
      toast({
        title: "Error",
        description: "Invalid timezone selected",
        variant: "destructive"
      });
      return;
    }

    setIsUpdatingTimezone(true);
    try {
      await updateBrandField(brandId, 'timezone', newTimezone);
      setSelectedBrand({ ...selectedBrand, timezone: newTimezone });
      setSelectedTimezone(newTimezone);
      
      toast({
        title: "Success",
        description: `Timezone updated to ${getTimezoneDisplayName(newTimezone)}`
      });
    } catch (error) {
      console.error('Error updating timezone:', error);
      toast({
        title: "Error",
        description: "Failed to update timezone",
        variant: "destructive"
      });
      // Revert the local state on error
      setSelectedTimezone(selectedBrand?.timezone || 'America/Chicago');
    } finally {
      setIsUpdatingTimezone(false);
    }
  };

  const handleSaveBrandName = async () => {
    if (!brandId || !brandNameValue.trim()) {
      toast({
        title: "Error",
        description: "Brand name cannot be empty",
        variant: "destructive"
      });
      return;
    }

    try {
      await updateBrandField(brandId, 'brand_name', brandNameValue.trim());
      setSelectedBrand({ ...selectedBrand, brand_name: brandNameValue.trim() });
      setEditingBrandName(false);
      toast({
        title: "Success",
        description: "Brand name updated successfully"
      });
    } catch (error) {
      console.error('Error updating brand name:', error);
      toast({
        title: "Error",
        description: "Failed to update brand name",
        variant: "destructive"
      });
    }
  };

  const handleCancelEdit = () => {
    setBrandNameValue(selectedBrand?.brand_name || '');
    setEditingBrandName(false);
  };

  const handleSaveUrl = async () => {
    await updateBrandUrl(brandUrl);
  };

  const isValidUrl = (url: string): boolean => {
    if (!url.trim()) return true; // Empty is valid (optional field)
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSaveLandingPage = async () => {
    if (!brandId) {
      toast({
        title: "Error",
        description: "Brand ID is missing",
        variant: "destructive"
      });
      return;
    }

    // Validate URL format if provided
    const trimmedValue = landingPageValue.trim();
    if (trimmedValue && !isValidUrl(trimmedValue)) {
      toast({
        title: "Error",
        description: "Please enter a valid URL (e.g., https://example.com/landing)",
        variant: "destructive"
      });
      return;
    }

    try {
      await updateBrandField(brandId, 'default_landing_page', trimmedValue || null);
      setSelectedBrand({ ...selectedBrand, default_landing_page: trimmedValue || null });
      setEditingLandingPage(false);
      toast({
        title: "Success",
        description: trimmedValue
          ? "Default landing page updated successfully"
          : "Default landing page cleared (will use main website URL)"
      });
    } catch (error) {
      console.error('Error updating default landing page:', error);
      toast({
        title: "Error",
        description: "Failed to update default landing page",
        variant: "destructive"
      });
    }
  };

  const handleCancelLandingPageEdit = () => {
    setLandingPageValue(selectedBrand?.default_landing_page || '');
    setEditingLandingPage(false);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Section Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-3 mb-4">
          <div className="p-3 bg-white/80 backdrop-blur-sm rounded-xl shadow-lg">
            <Settings className="h-6 w-6 text-blue-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-800 to-indigo-700 bg-clip-text text-transparent mb-2">
          Global Settings
        </h2>
        <p className="text-gray-600">
          Configure core settings for your brand
        </p>
      </div>

      {/* Brand Name Card */}
      <div className="group relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-300">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-50/20 via-transparent to-indigo-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="relative p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-2 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-lg shadow-sm">
              <Edit3 className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Brand Name</h3>
              <p className="text-sm text-gray-600">
                Change the name of your brand. This will be displayed throughout the application.
              </p>
            </div>
          </div>
          
          <div className="bg-gray-50/50 rounded-lg p-4">
            {editingBrandName ? (
              <div className="space-y-3">
                <Input
                  value={brandNameValue}
                  onChange={(e) => setBrandNameValue(e.target.value)}
                  placeholder="Enter brand name"
                  className="bg-white border-gray-200 focus:border-blue-400"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveBrandName();
                    } else if (e.key === 'Escape') {
                      handleCancelEdit();
                    }
                  }}
                />
                <div className="flex gap-2">
                  <Button 
                    onClick={handleSaveBrandName} 
                    size="sm"
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-sm"
                  >
                    <Save className="h-3 w-3 mr-2" />
                    Save Changes
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleCancelEdit} 
                    size="sm"
                    className="border-gray-200 hover:bg-gray-50"
                  >
                    <X className="h-3 w-3 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge className="bg-blue-100 text-blue-700 border-0 px-3 py-1">
                    Current Name
                  </Badge>
                  <span className="text-lg font-medium text-gray-900">
                    {selectedBrand?.brand_name || 'Untitled Brand'}
                  </span>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => setEditingBrandName(true)} 
                  size="sm"
                  className="bg-white hover:bg-blue-50 border-blue-200 text-blue-600 hover:text-blue-700"
                >
                  <Edit3 className="h-3 w-3 mr-2" />
                  Edit Name
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Timezone Settings Card */}
      <div className="group relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-300">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-50/20 via-transparent to-indigo-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="relative p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-2 bg-gradient-to-br from-purple-100 to-indigo-200 rounded-lg shadow-sm">
              <Clock className="h-5 w-5 text-purple-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Timezone</h3>
              <p className="text-sm text-gray-600">
                Set your brand's timezone for accurate content scheduling and "tomorrow's content today" email delivery.
              </p>
            </div>
            <Badge className="bg-purple-100 text-purple-700 border-0 px-2 py-1 text-xs">
              <Clock className="h-3 w-3 mr-1" />
              Scheduling
            </Badge>
          </div>
          
          <div className="space-y-4">
            <div className="bg-purple-50/30 rounded-lg p-4">
              <label className="text-sm font-medium text-gray-700 mb-3 block">Brand Timezone</label>
              <div className="space-y-3">
                <Select 
                  value={selectedTimezone} 
                  onValueChange={handleTimezoneChange}
                  disabled={isUpdatingTimezone || !brandId}
                >
                  <SelectTrigger className="bg-white border-purple-200 focus:border-purple-400">
                    <SelectValue placeholder="Select timezone..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {getTimezoneOptions().map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex flex-col">
                          <span className="font-medium">{option.label}</span>
                          {option.group && (
                            <span className="text-xs text-muted-foreground">{option.group}</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {selectedTimezone && (
                  <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg border border-purple-200/50">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-purple-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Current Time</p>
                        <p className="text-xs text-gray-600">{getTimezoneDisplayName(selectedTimezone)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-gray-900">
                        {formatTimeInTimezone(getCurrentTimeInTimezone(selectedTimezone), selectedTimezone)}
                      </p>
                      <p className="text-xs text-gray-600">
                        {getCurrentTimeInTimezone(selectedTimezone).toLocaleDateString('en-US', { 
                          timeZone: selectedTimezone,
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-3 p-3 bg-blue-50/50 rounded-lg">
                <div className="flex items-start gap-2">
                  <div className="p-1 bg-blue-200/50 rounded">
                    <Settings className="h-3 w-3 text-blue-600" />
                  </div>
                  <div className="text-xs text-blue-800">
                    <p className="font-medium mb-1">How this affects your content:</p>
                    <ul className="space-y-1 list-disc list-inside text-blue-700">
                      <li>Daily content digest emails use this timezone to determine "today" and "tomorrow"</li>
                      <li>Autoposter schedules content based on your local time</li>
                      <li>All scheduling features will respect this timezone setting</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {isUpdatingTimezone && (
                <div className="mt-3 flex items-center gap-2 text-sm text-purple-600">
                  <RefreshCw className="h-3 w-3 animate-spin" />
                  Updating timezone...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Website Analyzer Card */}
      <div className="group relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-300">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-50/20 via-transparent to-teal-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="relative p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-2 bg-gradient-to-br from-emerald-100 to-teal-200 rounded-lg shadow-sm">
              <Globe className="h-5 w-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Website Analyzer</h3>
              <p className="text-sm text-gray-600">
                Use an existing website to analyze your brand and import brand resources automatically.
              </p>
            </div>
            {brandUrl && (
              <Badge className="bg-green-100 text-green-700 border-0 px-2 py-1 text-xs">
                <CheckCircle className="h-3 w-3 mr-1" />
                Connected
              </Badge>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32 bg-gray-50/50 rounded-lg">
              <div className="flex flex-col items-center gap-3">
                <RefreshCw className="h-8 w-8 text-emerald-600 animate-spin" />
                <span className="text-sm text-gray-600">Loading website data...</span>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-emerald-50/30 rounded-lg p-4">
                <label className="text-sm font-medium text-gray-700 mb-2 block">Website URL</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      value={brandUrl}
                      onChange={(e) => setBrandUrl(e.target.value)}
                      placeholder="https://yourbrand.com"
                      className="pl-10 bg-white border-emerald-200 focus:border-emerald-400"
                    />
                    <Globe className="absolute left-3 top-2.5 h-4 w-4 text-emerald-500" />
                  </div>
                  <Button 
                    onClick={handleSaveUrl}
                    className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-sm"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save URL
                  </Button>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  We'll analyze this website to extract brand colors, logos, and content style
                </p>
              </div>

              {brandUrl && (
                <div className="flex items-center justify-between p-3 bg-gray-50/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Sparkles className="h-4 w-4 text-amber-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">AI Analysis Available</p>
                      <p className="text-xs text-gray-600">Extract brand assets and style guidelines</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline"
                    onClick={reanalyzeWebsite}
                    disabled={isReanalyzing}
                    className={`${
                      isReanalyzing 
                        ? 'opacity-70 cursor-not-allowed' 
                        : 'bg-white hover:bg-amber-50 border-amber-200 text-amber-600 hover:text-amber-700'
                    }`}
                    size="sm"
                  >
                    {isReanalyzing ? (
                      <>
                        <RefreshCw className="h-3 w-3 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Zap className="h-3 w-3 mr-2" />
                        Re-Analyze
                      </>
                    )}
                  </Button>
                </div>
              )}

              {!brandUrl && (
                <div className="flex items-start gap-3 p-3 bg-blue-50/50 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">No website connected</p>
                    <p className="text-xs">Add your website URL to unlock AI-powered brand analysis</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Default Landing Page Card */}
      <div className="group relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-300">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-50/20 via-transparent to-amber-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="relative p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-2 bg-gradient-to-br from-orange-100 to-amber-200 rounded-lg shadow-sm">
              <ExternalLink className="h-5 w-5 text-orange-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Default Landing Page</h3>
              <p className="text-sm text-gray-600">
                Set the default page users will land on when clicking links in your generated content.
              </p>
            </div>
          </div>

          <div className="bg-orange-50/30 rounded-lg p-4">
            {editingLandingPage ? (
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Landing Page URL</label>
                  <Input
                    value={landingPageValue}
                    onChange={(e) => setLandingPageValue(e.target.value)}
                    placeholder="https://yourbrand.com/landing"
                    className="bg-white border-orange-200 focus:border-orange-400"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveLandingPage();
                      } else if (e.key === 'Escape') {
                        handleCancelLandingPageEdit();
                      }
                    }}
                  />
                  <p className="text-xs text-gray-600 mt-2">
                    Press Enter to save, Escape to cancel
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleSaveLandingPage}
                    size="sm"
                    className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white shadow-sm"
                  >
                    <Save className="h-3 w-3 mr-2" />
                    Save Changes
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleCancelLandingPageEdit}
                    size="sm"
                    className="border-gray-200 hover:bg-gray-50"
                  >
                    <X className="h-3 w-3 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <Badge className="bg-orange-100 text-orange-700 border-0 px-3 py-1">
                      Current Landing Page
                    </Badge>
                    {landingPageValue ? (
                      <div className="flex items-center gap-2">
                        <ExternalLink className="h-4 w-4 text-orange-600" />
                        <span className="text-sm font-medium text-gray-900 truncate max-w-md">
                          {landingPageValue}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-600 italic">
                        Not set (using main website URL)
                      </span>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setEditingLandingPage(true)}
                    size="sm"
                    className="bg-white hover:bg-orange-50 border-orange-200 text-orange-600 hover:text-orange-700 ml-4"
                  >
                    <Edit3 className="h-3 w-3 mr-2" />
                    Edit
                  </Button>
                </div>

                <div className="p-3 bg-blue-50/50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <div className="p-1 bg-blue-200/50 rounded">
                      <Settings className="h-3 w-3 text-blue-600" />
                    </div>
                    <div className="text-xs text-blue-800">
                      <p className="font-medium mb-1">How this is used:</p>
                      <ul className="space-y-1 list-disc list-inside text-blue-700">
                        <li>Links in generated social media posts will direct to this page</li>
                        <li>Blog post CTAs will use this as the primary action link</li>
                        <li>If empty, defaults to your main website URL ({brandUrl || 'not set'})</li>
                      </ul>
                      <p className="mt-2 font-medium">Use cases:</p>
                      <ul className="space-y-1 list-disc list-inside text-blue-700">
                        <li>Campaign-specific landing pages</li>
                        <li>Product launch pages</li>
                        <li>Special promotional pages</li>
                        <li>Lead capture or sign-up pages</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

// Add missing import that was accidentally removed
import { Settings } from 'lucide-react';

export default GlobalSettingsTab;